import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddBoxIcon from '@mui/icons-material/AddBox';
import GroupsIcon from '@mui/icons-material/Groups';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { formatSandboxPrice, resolveSandboxMediaUrl, type SandboxCourse } from '../../utils/sandboxCourseApi';

function courseEarningsCents(course: SandboxCourse) {
    return Math.round((Number(course.enrollment_count) || 0) * (Number(course.price_cents) || 0));
}

function curriculumProgress(course: SandboxCourse) {
    const lessons = (course.modules || []).flatMap(m => m.lessons || []);
    if (!lessons.length) return { ready: 0, total: 0, percent: 0 };
    const ready = lessons.filter(l => l.project).length;
    return { ready, total: lessons.length, percent: Math.round((ready / lessons.length) * 100) };
}

type Props = {
    course: SandboxCourse;
    onOpenCourse: () => void;
    onEdit: () => void;
    onAddClass: () => void;
    onManageAssets: () => void;
    onPublish: () => void;
};

export function SandboxStudioCourseCard({
    course,
    onOpenCourse,
    onEdit,
    onAddClass,
    onManageAssets,
    onPublish,
}: Props) {
    const progress = curriculumProgress(course);
    const live = course.status === 'published';
    const coverUrl = resolveSandboxMediaUrl(course.cover_image);

    return (
        <Box sx={{ borderRadius: 3, bgcolor: OBSIDIAN.surfaceContainer, overflow: 'hidden' }}>
            <Box
                onClick={onOpenCourse}
                sx={{
                    position: 'relative',
                    height: 160,
                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                    cursor: 'pointer',
                    overflow: 'hidden',
                }}>
                {coverUrl ? (
                    <Box
                        component="img"
                        src={coverUrl}
                        alt=""
                        onError={e => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `radial-gradient(ellipse at 60% 30%, rgba(255,201,101,0.12), transparent 55%)`,
                        }}>
                        <Typography
                            sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                            No cover yet — use Edit Details
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, #1a1919, rgba(26,25,25,0.45), transparent)',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(8px)',
                        color: live ? OBSIDIAN.secondary : OBSIDIAN.primary,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: OBSIDIAN_BODY_FONT,
                    }}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: live ? OBSIDIAN.secondary : OBSIDIAN.primary,
                        }}
                    />
                    {live ? 'Live Curriculum' : 'Draft in Progress'}
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        left: 12,
                        right: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        gap: 1,
                    }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 16,
                            color: OBSIDIAN.onSurface,
                            lineHeight: 1.25,
                        }}>
                        {course.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 800,
                            fontSize: 14,
                            color: OBSIDIAN.primary,
                            flexShrink: 0,
                        }}>
                        {live
                            ? formatSandboxPrice(courseEarningsCents(course), course.currency)
                            : `${progress.ready}/${progress.total || 0}`}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {live ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            flexWrap: 'wrap',
                            gap: 1,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <GroupsIcon sx={{ fontSize: 16, color: OBSIDIAN.primary }} />
                            {course.enrollment_count || 0} Enrolled
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: OBSIDIAN.secondary }} />
                            {progress.ready} of {progress.total || 0} Classes
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                                Curriculum Completion
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: OBSIDIAN.primary }}>
                                {progress.percent}%
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                height: 6,
                                borderRadius: 999,
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                overflow: 'hidden',
                            }}>
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${progress.percent}%`,
                                    borderRadius: 999,
                                    background: `linear-gradient(90deg, ${OBSIDIAN.primary}, ${OBSIDIAN.secondary})`,
                                }}
                            />
                        </Box>
                    </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        fullWidth
                        startIcon={<EditNoteIcon sx={{ fontSize: 16 }} />}
                        onClick={e => {
                            e.stopPropagation();
                            onEdit();
                        }}
                        sx={{
                            py: 1,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            color: OBSIDIAN.onSurface,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                        }}>
                        Edit Details
                    </Button>
                    <Button
                        fullWidth
                        startIcon={<AddBoxIcon sx={{ fontSize: 16 }} />}
                        onClick={e => {
                            e.stopPropagation();
                            onAddClass();
                        }}
                        sx={{
                            py: 1,
                            borderRadius: 2,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            color: OBSIDIAN.primary,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                        }}>
                        Add Class
                    </Button>
                </Box>
                <Button
                    fullWidth
                    startIcon={<FolderZipOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={e => {
                        e.stopPropagation();
                        onManageAssets();
                    }}
                    sx={{
                        py: 1,
                        borderRadius: 2,
                        bgcolor: OBSIDIAN.surfaceContainerHighest,
                        color: OBSIDIAN.onSurface,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 12,
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                    }}>
                    Class Assets
                    {(course.class_assets?.length || course.class_asset_count || 0) > 0
                        ? ` (${course.class_assets?.length || course.class_asset_count})`
                        : ''}
                </Button>
                {course.status !== 'published' && (
                    <Button
                        fullWidth
                        onClick={e => {
                            e.stopPropagation();
                            onPublish();
                        }}
                        sx={{
                            py: 1.25,
                            borderRadius: 2,
                            background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                            color: OBSIDIAN.onPrimaryFixed,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 12,
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                        }}>
                        Publish Course
                    </Button>
                )}
            </Box>
        </Box>
    );
}
